import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAdoptedTools, iconFor, toolName } from "../../../marketplace";
import type { TherapyTool } from "../../../marketplace";
import { PaymentsDockActions } from "../../../payments";
import ToolDock, { type ToolDockGroup } from "../../../../shared/ui/ToolDock";

/**
 * Tools a therapist can reach from a session.
 *
 * Only Payments today. Agendar and the constellation board are scoped to a
 * patient rather than to one appointment, so they stay on the patient dock —
 * a dock that lists tools it cannot usefully scope is worse than a short one.
 */
const DOCKED_SLUGS = new Set(["payments"]);

interface Props {
    sessionId: string;
    /**
     * Absent on a couple session. A payment is owed by an individual and the
     * API takes a patient id, so the dock hides rather than opening a form
     * that cannot be submitted.
     */
    patientId?: string | null;
    patientLabel?: string | null;
}

export default function SessionToolDock({
    sessionId,
    patientId,
    patientLabel,
}: Props) {
    const { t } = useTranslation();
    const { data: tools, isLoading } = useAdoptedTools();
    const [openSheetSlug, setOpenSheetSlug] = useState<string | null>(null);

    if (isLoading || !tools || !patientId) return null;

    const docked: TherapyTool[] = tools.filter((tool) =>
        DOCKED_SLUGS.has(tool.slug)
    );
    if (docked.length === 0) return null;

    // The same dock as home and the patient page — at the bottom, and wherever
    // the therapist has moved it — rather than a rail of its own on the right.
    const groups: ToolDockGroup[] = [
        {
            key: "session",
            label: t("constellation.dock.title"),
            items: docked.map((tool) => {
                const Icon = iconFor(tool.icon);
                return {
                    id: tool.id,
                    label: toolName(t, tool),
                    icon: <Icon className="h-5 w-5" />,
                    onSelect: () => setOpenSheetSlug(tool.slug),
                };
            }),
        },
    ];

    return (
        <>
            <ToolDock
                groups={groups}
                ariaLabel={t("constellation.dock.title")}
            />

            <PaymentsDockActions
                isOpen={openSheetSlug === "payments"}
                onClose={() => setOpenSheetSlug(null)}
                subject={{
                    id: patientId,
                    label: patientLabel ?? t("payments.launchSheet.thisPatient"),
                }}
                // What the removed inline button carried: the charge stays
                // linked to the session it was raised for.
                sessionId={sessionId}
            />
        </>
    );
}
